import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStaff } from "@/context/StaffContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  ArrowRight, ArrowLeft, ShieldAlert, Lock, LifeBuoy,
} from "lucide-react";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

export default function StaffLogin() {
  const { t } = useTranslation();
  const { beginLogin, verify2fa } = useStaff();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitCreds = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await beginLogin(email, password);
    setLoading(false);
    if (res.ok) {
      if (res.bypassed) {
        navigate("/staff");
        return;
      }
      setChallenge(res.challenge_token);
      setStep(2);
    } else setError(res.error);
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verify2fa(challenge, otp);
    setLoading(false);
    if (res.ok) navigate("/staff");
    else setError(res.error);
  };

  return (
    <div
      className="min-h-screen bg-[#0B0F17] text-slate-100 antialiased relative overflow-hidden"
      style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', system-ui, sans-serif" }}
      data-testid="staff-login-page"
    >
      {/* Decorative background */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#FFF 1px, transparent 1px), linear-gradient(90deg, #FFF 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 70% 90% at 50% 40%, #000 30%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 90% at 50% 40%, #000 30%, transparent 100%)",
        }}
      />
      <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-1/4 h-96 w-96 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />

      <div className="relative min-h-screen flex flex-col items-center justify-center p-6">
        {/* Brand */}
        <Link to="/welcome" className="flex items-center gap-3 mb-8 group">
          <BrandLogo variant="full" className="h-11" />
          <span className="h-6 w-px bg-white/20" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-amber-400">
            Staff
          </span>
        </Link>

        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 sm:p-8 shadow-2xl shadow-black/40">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1 ring-amber-500/30 bg-amber-500/10 mb-5">
              <Lock className="h-3 w-3 text-amber-400" />
              <span className="text-[11px] font-semibold text-amber-300 tracking-wide">
                Portail interne · accès restreint
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white leading-[1.15]">
              {step === 1 ? t("auth.staffLogin") : t("auth.totpTitle")}
            </h1>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              {step === 1 ? t("staff.subtitle") : t("auth.totpSubtitle")}
            </p>

            {error && (
              <Alert
                variant="destructive"
                className="mt-5 bg-red-950/40 border-red-800 text-red-200"
                data-testid="staff-error"
              >
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === 1 && (
              <form onSubmit={submitCreds} className="mt-6 space-y-4" data-testid="staff-login-form">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                    {t("common.email")}
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="staff@averdy.agency"
                    data-testid="staff-email-input"
                    className="h-11 bg-[#0B0F17] border-white/10 text-white placeholder:text-slate-500 focus-visible:border-amber-500 focus-visible:ring-amber-500/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                    {t("common.password")}
                  </Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    data-testid="staff-password-input"
                    className="h-11 bg-[#0B0F17] border-white/10 text-white placeholder:text-slate-500 focus-visible:border-amber-500 focus-visible:ring-amber-500/20"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="staff-continue-btn"
                  className={cn(
                    "group w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold transition-all",
                    "bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-lg shadow-amber-500/20",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                  )}
                >
                  {loading ? t("common.loading") : t("common.continue")}
                  {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={submit2fa} className="mt-6 space-y-5" data-testid="staff-2fa-form">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="staff-2fa-otp">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          className="border-white/15 text-white bg-[#0B0F17]"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button
                  type="submit"
                  disabled={loading || otp.length < 6}
                  data-testid="staff-verify-2fa-btn"
                  className={cn(
                    "group w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold transition-all",
                    "bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-lg shadow-amber-500/20",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                  )}
                >
                  {loading ? t("common.loading") : t("auth.totpVerify")}
                </button>
              </form>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
            <Link
              to="/login"
              data-testid="staff-back-to-client"
              className="inline-flex items-center gap-1.5 hover:text-slate-300 transition-colors"
            >
              <ArrowLeft className="h-3 w-3" />
              Retour au portail client
            </Link>
            <span className="inline-flex items-center gap-1.5">
              <LifeBuoy className="h-3 w-3 text-amber-400" />
              Portail Staff Averdy
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
